import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ClientConstraints } from '@/hooks/providers/useClientConstraintsProvider';
import Slider from '@/components/Slider/Slider';
import styles from './Calculator.module.scss';
import useEventCollector from '@/hooks/context/EventCollector/useEventCollector';
import CalculatorEvent from '@/hooks/context/EventCollector/events/CalculatorEvent';

export type CalculatorProps = {
  constraints: ClientConstraints;
  calculatorAmount?: number;
  onAmountChange?: (value: number) => void;
  dataTest?: string;
};

const Calculator = memo(function Calculator({
  onAmountChange,
  constraints,
  calculatorAmount,
  dataTest = 'calculator',
}: CalculatorProps) {
  const { t } = useTranslation(['calculator', 'common']);
  const { track } = useEventCollector();

  const handleAmountChange = useCallback(
    (value: number) => {
      onAmountChange?.(value);
      track(
        new CalculatorEvent({
          description: 'calculator amount change',
          value,
        }),
      );
    },
    [onAmountChange, track],
  );

  return (
    <div data-test={dataTest} className={styles['calculator-wrapper']}>
      <Slider
        dataTest="amount-slider"
        min={constraints.amountInterval.min}
        max={constraints.amountInterval.max}
        step={constraints.amountInterval.step}
        value={
          calculatorAmount !== undefined
            ? calculatorAmount
            : constraints.amountInterval.defaultValue
        }
        onAfterChange={handleAmountChange}
        units={t('common:currency')}
        heading={t('amount_heading')}
        className={styles.slider}
      />
    </div>
  );
});

export default Calculator;
